import { useState } from "react";
import { Modal, Box, IconButton, Tooltip, Grid } from "@mui/material";
import { Close, Dashboard, FileDownload, GridView, Link, ViewSidebar } from "@mui/icons-material";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { thumbnailPlugin } from "@react-pdf-viewer/thumbnail";

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/page-navigation/lib/styles/index.css";
import "@react-pdf-viewer/thumbnail/lib/styles/index.css";

import { ButtonV1 } from "../atoms/button";
import CopyToClipboard from "react-copy-to-clipboard";
import { CreativeType } from "../../types/types";
import "./PdfViewer.css";

interface PdfViewerModalProps {
  open: boolean;
  onClose: () => void;
  item: CreativeType
}

interface PdfViewerModalProps2 {
  open: boolean;
  onClose: () => void;
  pdfLink: string;
  pptLink: string;
}
export const PdfViewerModal: React.FC<PdfViewerModalProps> = ({
  open,
  onClose,
  item,
}) => {
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { CurrentPageInput, GoToNextPage, GoToPreviousPage, NumberOfPages } =
    pageNavigationPluginInstance;

  const thumbnailPluginInstance = thumbnailPlugin();
  const { Thumbnails } = thumbnailPluginInstance;

  const [copy, setCopy] = useState(false);
  const [showThumbnails, setShowThumbnails] = useState(false);

  const newTitle = encodeURI(item?.title).replaceAll("+", "%2B");
  const shareableUrl = `${window.location.origin}/presentation?creativeId=${item._id}`;

  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "95vw",
    maxWidth: "1200px",
    height: "90vh",
    bgcolor: "background.paper",
    borderRadius: "1.5rem",
    boxShadow: 24,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        {/* Header */}
        <div className="pdf-viewer flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-2">

            <Tooltip title={showThumbnails ? "Hide thumbnails" : "Show thumbnails"}>
              <IconButton
                onClick={() => setShowThumbnails((prev) => !prev)}
                size="small"
              >
                <GridView />
              </IconButton>
            </Tooltip>
            <div className="flex gap-1 items-center textsPdf font-sans">
              <GoToPreviousPage />
              <CurrentPageInput /> <span>|</span> <span><NumberOfPages /></span>
              <GoToNextPage />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div
              className="absolute right-0.5 top-0.5 bg-black/40 hover:bg-black/60 rounded-full cursor-pointer p-2 z-20 transition-all duration-200"
              onClick={onClose}
            >
              <Close className="text-white !text-3xl" />
            </div>
          </div>
        </div>

        {/* PDF Viewer + Thumbnails */}
        <div className="flex flex-1 overflow-hidden bg-gray-100">
          {/* Thumbnails (left) */}
          <div
            className={`transition-all duration-300 border-r border-gray-200 overflow-auto hidden md:block ${showThumbnails ? "w-1/5" : "w-0"
              }`}
          >
            {showThumbnails && <Thumbnails />}
          </div>

          {/* Main Viewer (always full width, fills remaining space) */}
          <div className={`flex-1 transition-all duration-300 ${showThumbnails ? "w-4/5  max-w-[80%]" : "w-full"}`}>
            {item?.pptDetails?.pdfLink && item.pptDetails.pdfLink.trim() !== "" ? (
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                <Viewer
                  fileUrl={item.pptDetails.pdfLink}
                  defaultScale={0.75}
                  plugins={[pageNavigationPluginInstance, thumbnailPluginInstance]}
                />
              </Worker>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">No PDF available to display</p>
              </div>
            )}
          </div>
        </div>



        {/* Footer */}
        <div className="w-full flex gap-2 justify-between items-center bg-gray-50 border-t border-gray-200">
          <div className="flex-1 flex flex-col px-4 py-2">
            <div className="flex items-center gap-1 font-medium text-sm text-gray-700">
              <span
                className={`w-2 h-2 rounded-full ${item.stage === "postsale" ? "bg-black" : "bg-gray-500"
                  }`}
              ></span>
              {item.stage}
              {item.dt ? ` | ${item.dt}` : ""}
            </div>
            <h1 className="truncate text-xl font-medium">{item.title}</h1>
          </div>

          <div className="flex gap-3 px-4">
            <ButtonV1>
              <CopyToClipboard
                text={shareableUrl}
                onCopy={() => setCopy(true)}
              >
                {copy ? (
                  <p className="capitalize text-xs">Copied</p>
                ) : (
                  <Link className="text-white" />
                )}
              </CopyToClipboard>
            </ButtonV1>
            <ButtonV1>
              <a
                href={item?.pptDetails?.pptLink}
                onClick={() => setCopy(false)}
                download
              >
                <FileDownload className="text-white" />
              </a>
            </ButtonV1>
          </div>
        </div>
        <div className="bottom-pillar"></div>
      </Box>
    </Modal>
  );
};

export const PdfViewerModalV2: React.FC<PdfViewerModalProps2> = ({
  open,
  onClose,
  pdfLink,
  pptLink,
}) => {
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { CurrentPageInput, GoToNextPage, GoToPreviousPage, NumberOfPages } =
    pageNavigationPluginInstance;


  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "70vw",
    height: "90vh",
    bgcolor: "background.paper",
    borderRadius: "1.5rem",
    boxShadow: 24,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",

  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={style}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex gap-1 items-center">
            <GoToPreviousPage />
            <CurrentPageInput /> <span>/</span> <NumberOfPages />
            <GoToNextPage />
          </div>
          <div className="flex items-center space-x-2">
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </div>
        </div>

        {/* PDF Viewer */}
        <div className="flex-1 overflow-auto bg-gray-100">
          {pdfLink && pdfLink.trim() !== "" ? (
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
              <Viewer
                fileUrl={pdfLink}
                defaultScale={0.8}
                plugins={[pageNavigationPluginInstance]}
              />
            </Worker>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">No PDF available to display</p>
            </div>
          )}
        </div>
      </Box>
    </Modal>
  );
};